import React, { useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService, { Credentials } from '../services/apiService';

import {
    containerStyle,
    formContainerStyle,
    headerStyle,
    titleStyle,
    formStyle,
    errorStyle,
    buttonStyle,
    footerTextStyle,
    inputStyle
} from '../styles/globalStyles';

const VerifyCode: React.FC = () => {
    const [digits, setDigits] = useState<string[]>( [ '', '', '', '', '', '' ] );
    const [error, setError] = useState( '' );
    const [isSubmitting, setIsSubmitting] = useState( false );
    const [isResending, setIsResending] = useState( false );
    const [message, setMessage] = useState( '' );
    const inputRefs = useRef<Array<HTMLInputElement | null>>( [] );

    const navigate = useNavigate();

    const credentials = useMemo< Credentials >( () => ( {
        email: process.env.REACT_APP_ADMIN_USERNAME || '',
        password: process.env.REACT_APP_ADMIN_PASSWORD || ''
    } ), [] );

    const apiService = useMemo( () => new ApiService( credentials ), [ credentials ] );

    const getPendingRegistration = () => {
        const storedData = localStorage.getItem( 'pendingRegistration' );

        if( ! storedData )
            return null;

        try{
            return JSON.parse( storedData );
        }catch( err ){
            return null;
        }
    };

    const handleDigitChange = ( index: number, value: string ) => {
        const digit = value.replace( /\D/g, '' ).slice( -1 );
        const nextDigits = [ ...digits ];
        nextDigits[ index ] = digit;
        setDigits( nextDigits );
        setError( '' );
        setMessage( '' );

        if( digit && index < 5 ){
            inputRefs.current[ index + 1 ]?.focus();
        }

        if( nextDigits.every( (entry) => entry !== '' ) )
            submitCode( nextDigits.join( '' ) );
    };

    const handleKeyDown = ( index: number, event: React.KeyboardEvent<HTMLInputElement> ) => {
        if( event.key === 'Backspace' && digits[ index ] === '' && index > 0 ){
            inputRefs.current[ index - 1 ]?.focus();
        }
    };

    const handlePaste = ( event: React.ClipboardEvent<HTMLInputElement> ) => {
        const pasted = event.clipboardData.getData( 'Text' ).replace( /\D/g, '' ).slice( 0, 6 );

        if( pasted.length === 0 )
            return;

        event.preventDefault();

        const nextDigits = [ '', '', '', '', '', '' ];
        for( let i = 0; i < pasted.length; i ++ )
            nextDigits[ i ] = pasted[ i ];

        setDigits( nextDigits );
        setError( '' );
        setMessage( '' );

        if( pasted.length < 6 ){
            inputRefs.current[ pasted.length ]?.focus();
        }else{
            submitCode( pasted );
        }
    };

    const submitCode = async ( verificationCode: string ) => {
        if( isSubmitting )
            return;

        const pendingRegistration = getPendingRegistration();

        if( ! pendingRegistration ){
            setError( 'Registration data is missing. Please start over.' );
            return;
        }

        setIsSubmitting( true );
        setError( '' );

        try{
            const response = await apiService.request< boolean >( 'POST', '/api/v1/user/complete-registration', { body: {
                ...pendingRegistration,
                verification_token: verificationCode
            } } );

            if( ! response ){
                setError( 'Verification failed. Please try again.' );
                return;
            }

            localStorage.removeItem( 'pendingRegistration' );
            navigate( '/login' );
        }catch( err ){
            setError( 'Verification failed. Please try again.' );
        }finally{
            setIsSubmitting( false );
        }
    };

    const handleResendCode = async () => {
        if( isResending )
            return;

        const pendingRegistration = getPendingRegistration();

        if( ! pendingRegistration ){
            setError( 'Registration data is missing. Please start over.' );
            return;
        }

        setIsResending( true );
        setError( '' );
        setMessage( '' );

        try{
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/user/resend-verification-email/${encodeURIComponent( pendingRegistration.email )}/${encodeURIComponent( pendingRegistration.name )}`,
                { method: 'POST' }
            );

            if( ! response.ok ){
                const errorData = await response.json();
                setError( errorData.detail || 'Failed to resend code. Please try again.' );
                return;
            }

            setMessage( 'Verification code resent.' );
        }catch( err ){
            setError( 'Failed to resend code. Please try again.' );
        }finally{
            setIsResending( false );
        }
    };

    return(
        <div style={containerStyle}>
            <div style={formContainerStyle}>
                <div style={headerStyle}>
                    <h1 style={titleStyle}>Enter Verification Code</h1>
                </div>
                <div style={formStyle}>
                    {( error || message ) && (
                        <div style={{ ...errorStyle, color: message ? '#22d3ee' : '#ef4444' }}>
                            {message || error}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                        {digits.map( (digit, index) => (
                            <input
                                key={`digit-${index}`}
                                ref={( element ) => { inputRefs.current[ index ] = element; }}
                                value={digit}
                                onChange={( event ) => handleDigitChange( index, event.target.value )}
                                onKeyDown={( event ) => handleKeyDown( index, event )}
                                onPaste={handlePaste}
                                inputMode="numeric"
                                maxLength={1}
                                style={{
                                    ...inputStyle,
                                    width: 48,
                                    textAlign: 'center',
                                    fontSize: 22,
                                    border: '2px solid #22d3ee'
                                }}
                            />
                        ))}
                    </div>
                    <button type="button" style={buttonStyle} onClick={handleResendCode} disabled={isResending || isSubmitting}>
                        {isResending ? 'RESENDING...' : 'RESEND CODE'}
                    </button>
                </div>
                <div style={{ ...footerTextStyle, display: 'flex', flexDirection: 'column' as const, alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="18" height="18" fill="none" stroke="#b0b3b8" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 17v.01"/><path d="M12 13a4 4 0 1 0-4-4"/><circle cx="12" cy="12" r="10"/></svg>
                        Your information is used only for licensing and is never shared.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default VerifyCode;
